/**
 * API Client
 * Centralized Axios-based HTTP client with interceptors and error handling
 */

import axios, {
  type AxiosInstance,
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import * as Sentry from "@sentry/react";
import { config as appConfig } from "@/config";
import { ApiError, type ApiErrorResponse } from "./types";
import {
  getStoredToken,
  isTokenExpired,
  ensureValidToken,
  handleUnauthorized,
  AuthenticationError,
} from "@/feature/auth";

// Default timeout in milliseconds (30 seconds)
const DEFAULT_TIMEOUT = 30000;

// Flag to prevent concurrent token refresh attempts
let isRefreshingToken = false;
let tokenRefreshPromise: Promise<string> | null = null;

/**
 * Create and configure the Axios instance
 */
const createApiClient = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: appConfig.api.baseUrl,
    timeout: DEFAULT_TIMEOUT,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  // Request interceptor - add auth token, logging, etc.
  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // Skip auth for login endpoint
      if (config.url?.includes("/auth/login")) {
        return config;
      }

      // Check if token exists and is valid
      let token = getStoredToken();

      if (!token || isTokenExpired()) {
        // Ensure only one token validation happens at a time
        if (!isRefreshingToken) {
          isRefreshingToken = true;
          tokenRefreshPromise = ensureValidToken();
        }

        try {
          token = await tokenRefreshPromise;
        } catch (error) {
          // If authentication fails, redirect to login
          if (error instanceof AuthenticationError) {
            window.location.href = "/login";
          }
          throw error;
        } finally {
          isRefreshingToken = false;
          tokenRefreshPromise = null;
        }
      }

      // Add token to request header
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Log requests in debug mode
      if (appConfig.features.debug) {
        console.log(
          `🌐 API Request: ${config.method?.toUpperCase()} ${config.url}`,
        );
      }
      return config;
    },
    (error: AxiosError) => {
      console.error("❌ Request error:", error.message);
      return Promise.reject(error);
    },
  );

  // Response interceptor - handle errors, transform responses
  instance.interceptors.response.use(
    (response) => {
      // Log successful responses in debug mode
      if (appConfig.features.debug) {
        console.log(
          `✅ API Response: ${response.status} ${response.config.url}`,
        );
      }
      return response;
    },
    async (error: AxiosError<ApiErrorResponse>) => {
      const apiError = handleAxiosError(error);

      // Add Sentry breadcrumb for API errors
      Sentry.addBreadcrumb({
        category: "api",
        message: `${error.config?.method?.toUpperCase()} ${error.config?.url} → ${error.response?.status ?? "network error"}`,
        level: "error",
        data: {
          statusCode: error.response?.status,
          url: error.config?.url,
          method: error.config?.method,
        },
      });

      // Capture server errors (5xx) in Sentry
      if (apiError.isServerError()) {
        Sentry.captureException(apiError, {
          tags: {
            api_url: error.config?.url,
            api_method: error.config?.method,
            status_code: String(error.response?.status),
          },
        });
      }

      // Handle 401 - clear token and redirect to login
      if (error.response?.status === 401) {
        handleUnauthorized();
        window.location.href = "/login";
      }
      return Promise.reject(apiError);
    },
  );

  return instance;
};

/**
 * Transform Axios errors into ApiError instances
 */
const handleAxiosError = (error: AxiosError<ApiErrorResponse>): ApiError => {
  // Network error (no response received)
  if (!error.response) {
    if (error.code === "ECONNABORTED") {
      return new ApiError("Request timeout - please try again", 0, "TIMEOUT", {
        isTimeoutError: true,
      });
    }
    return new ApiError(
      "Network error - please check your connection",
      0,
      "NETWORK_ERROR",
      { isNetworkError: true }
    );
  }

  // Server responded with error
  const { status, data } = error.response;
  const message =
    data?.meta?.message || error.message || "An unexpected error occurred";
  const code = data?.meta?.code || String(status);
  const errors = data?.meta?.errors;

  return new ApiError(message, status, code, { errors });
};

/**
 * Singleton API client instance
 */
export const apiClient = createApiClient();

/**
 * Helper to create request config with optional params
 */
export const createRequestConfig = (
  params?: Record<string, string | number | boolean | undefined>,
  additionalConfig?: AxiosRequestConfig
): AxiosRequestConfig => {
  const config: AxiosRequestConfig = { ...additionalConfig };

  if (params) {
    // Filter out undefined values
    const filteredParams: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        filteredParams[key] = value;
      }
    }
    config.params = filteredParams;
  }

  return config;
};

export default apiClient;
